// src/hooks/useAdaptiveViewport.js
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Route + device adaptive viewport controller.
 * - On mobile devices visiting "companion" routes -> scale 1.0 and allow native pull-to-refresh
 * - Everywhere else (TV shell) -> index.html default (0.5) and restore TV touch locks
 */
const MOBILE_ROUTES = ["/signin", "/signup", "/pair", "/interact"];

function isMobileUA() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /Android|iPhone|iPad|iPod/i.test(ua);
}

export default function useAdaptiveViewport() {
  const { pathname } = useLocation();
  const appliedRef = useRef(false);

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;

    const isMobile = isMobileUA();
    const isMobileRoute = MOBILE_ROUTES.some((r) => pathname.startsWith(r));
    const wantsMobile = isMobile && isMobileRoute;

    // Viewport strings
    const mobileContent =
      "width=device-width, initial-scale=1.0, viewport-fit=cover, shrink-to-fit=no, user-scalable=yes";
    const tvContent =
      "width=device-width, initial-scale=0.5, shrink-to-fit=no, user-scalable=no";

    // Apply viewport for current route/device
    viewport.setAttribute("content", wantsMobile ? mobileContent : tvContent);

    // Re-enable native pull-to-refresh on mobile routes
    // Some TV-first styles disable it via overscroll/touch-action/overflow locks.
    const html = document.documentElement;
    const body = document.body;

    if (wantsMobile) {
      html.style.overscrollBehaviorY = "auto";
      body.style.overscrollBehaviorY = "auto";
      // iOS: enable momentum scrolling and rubber-band
      body.style.webkitOverflowScrolling = "touch";
      // Ensure the page can actually reach the top edge
      html.style.overflowY = "auto";
      body.style.overflowY = "auto";
      // Allow touch gestures (in case TV shell sets none)
      html.style.touchAction = "auto";
      body.style.touchAction = "auto";
      appliedRef.current = true;
    } else if (appliedRef.current) {
      // Restore control to TV shell by clearing only what we set
      html.style.overscrollBehaviorY = "";
      body.style.overscrollBehaviorY = "";
      body.style.webkitOverflowScrolling = "";
      html.style.overflowY = "";
      body.style.overflowY = "";
      html.style.touchAction = "";
      body.style.touchAction = "";
      appliedRef.current = false;
    }
  }, [pathname]);
}
